import { parisDateOf } from "./followup";
import { formatParis, parisHour } from "./time";

export type WaTemplate =
  | "bienvenue"
  | "confirmation"
  | "relance_chaud"
  | "premier_message"
  | "absence"
  | "relance";

export interface WaVars {
  firstName: string;
  /** ISO. Obligatoire pour `bienvenue` et `confirmation` ; facultatif pour `absence`. */
  rdvStart?: string;
  /** ISO UTC de la réservation (champ `created` de l'événement Google). Facultatif : sans lui, `confirmation` n'indique pas quand. */
  bookedAt?: string;
  bookingUrl: string;
}

// Variables : {prenom} {jour} {heure} {heure_courte} (« 10h », « 14h30 ») {moment} {quand} (quand la personne a réservé, voir bookedPhrase) {lien}. Heure toujours de Paris. Pas de tiret cadratin.
const TEMPLATES: Record<WaTemplate, string> = {
  bienvenue: "Hello {prenom}, c'est Christophe, de Power Coaching (le live de Maxime). On se parle {jour} à {heure} (heure de Paris) pour ta session plan d'action. Si une question te trotte dans la tête d'ici là, envoie-la moi ici. À bientôt !",
  confirmation: "Bonjour {prenom}, c'est Christophe, de l'équipe Power Coaching.\n\nPetit message pour vous rappeler notre session plan d'action {moment} que vous avez réservée après le webinaire avec Maxime ! 😊\n\nNotre RDV est à {heure_courte}. Je vous appellerai directement ici sur WhatsApp, c'est bon pour vous ?",
  relance_chaud: "Bonjour {prenom}, c'est Christophe, de Power Coaching (le live de Maxime). Merci d'avoir suivi le live ! Si tu veux appliquer le modèle à ton activité, je t'offre 30 min pour en faire un plan concret : {lien}",
  premier_message: "Bonjour {prenom}, c'est Christophe, de Power Coaching (le live de Maxime). Si tu veux appliquer le modèle à ton activité, je t'offre 30 min pour en faire un plan concret, sur ton cas : {lien}",
  absence: "Bonjour {prenom}, c'est Christophe, de Power Coaching. J'ai essayé de te joindre à {heure} pour ta session plan d'action, sans succès. Pas de souci : choisis un nouveau créneau ici : {lien}",
  relance: "Bonjour {prenom}, c'est Christophe, de Power Coaching. Je reviens vers toi suite à notre échange : tu as eu le temps d'y réfléchir ? Si tu veux, on cale 15 min pour faire le point : {lien}",
};

/** Moment de la journée d'après l'heure de Paris, pour « ta session de ce matin ». */
function momentOfDay(hour: number): string {
  if (hour < 12) return "de ce matin";
  if (hour < 18) return "de cet après-midi";
  return "de ce soir";
}

/** « 10 h 00 » devient « 10h », « 14 h 30 » devient « 14h30 ». */
function shortTime(t: string): string {
  return t.replace(/ h 00$/, "h").replace(" h ", "h");
}

const DAY_MS = 86_400_000;

/** Écart en jours calendaires de Paris entre deux instants ISO ; null si une date est illisible. */
function parisDayGap(fromIso: string, toIso: string): number | null {
  const a = parisDateOf(fromIso);
  const b = parisDateOf(toIso);
  if (!a || !b) return null;
  return Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / DAY_MS);
}

/**
 * Quand la personne a réservé, pour « que vous avez réservé{quand} après le webinaire ».
 * Vide si la date est absente, illisible ou postérieure au RDV ; sinon commence par une espace.
 */
export function bookedPhrase(bookedAt: string | undefined, rdvStart: string): string {
  if (!bookedAt) return "";
  const booked = Date.parse(bookedAt);
  const rdv = Date.parse(rdvStart);
  if (Number.isNaN(booked) || Number.isNaN(rdv) || booked > rdv) return "";
  const gap = parisDayGap(bookedAt, rdvStart);
  if (gap === null || gap < 0) return "";
  if (gap === 0) return " tout à l'heure";
  if (gap === 1) return " hier";
  if (gap <= 6) return ` ${formatParis(bookedAt).day.split(" ")[0]}`;
  if (gap <= 13) return " la semaine dernière";
  return " il y a quelques semaines";
}

/** Date de réservation (`created`) lue dans le JSON brut d'un événement Google ; undefined si absente ou non ISO. */
export function bookedAtOf(rawJson: string | null | undefined): string | undefined {
  if (!rawJson) return undefined;
  try {
    const created = (JSON.parse(rawJson) as { created?: unknown } | null)?.created;
    if (typeof created !== "string" || !/^\d{4}-\d{2}-\d{2}T/.test(created)) return undefined;
    return Number.isNaN(Date.parse(created)) ? undefined : created;
  } catch {
    return undefined;
  }
}

export function renderWhatsApp(t: WaTemplate, v: WaVars): string {
  if ((t === "bienvenue" || t === "confirmation") && !v.rdvStart) {
    throw new Error(`renderWhatsApp(${t}) exige rdvStart`);
  }
  const name = v.firstName.trim();
  const paris = v.rdvStart ? formatParis(v.rdvStart) : null;
  let text = TEMPLATES[t];
  // Sans heure, « à {heure} » disparaît (gabarit absence).
  if (!paris) text = text.replace(" à {heure}", "");
  return text
    .replaceAll(" {prenom}", name ? ` ${name}` : "")
    .replaceAll("{jour}", paris?.day ?? "")
    .replaceAll("{heure_courte}", paris ? shortTime(paris.time) : "")
    .replaceAll("{heure}", paris?.time ?? "")
    .replaceAll("{moment}", v.rdvStart ? momentOfDay(parisHour(v.rdvStart)) : "")
    .replaceAll("{quand}", v.rdvStart ? bookedPhrase(v.bookedAt, v.rdvStart) : "")
    .replaceAll("{lien}", v.bookingUrl);
}

/**
 * Numéro international sans « + », ou null si le numéro est douteux (un mauvais numéro est pire
 * qu'un bouton désactivé). Après retrait des non-chiffres :
 * - 10 chiffres commençant par 0 : le 0 devient 33 ;
 * - 9 chiffres commençant par 6 ou 7 : préfixe 33 ;
 * - commençant par 33 : 11 chiffres tels quels, 12 chiffres en 330 (« +33 (0)6 ») sans le 0, sinon null ;
 * - autre : conservé seulement entre 11 et 15 chiffres.
 */
export function waDigits(phone: string | null): string | null {
  if (!phone) return null;
  let d = phone.replace(/\D/g, "");
  if (d.startsWith("00")) d = d.slice(2);
  if (d.length === 10 && d.startsWith("0")) d = "33" + d.slice(1);
  else if (d.length === 9 && /^[67]/.test(d)) d = "33" + d;
  else if (d.startsWith("33")) {
    if (d.length === 12 && d.startsWith("330")) d = "33" + d.slice(3);
    else if (d.length !== 11) return null;
  } else if (d.length < 11 || d.length > 15) return null;
  return d;
}

/** Lien wa.me, ou null si le numéro est douteux (voir `waDigits`). */
export function waLink(phone: string | null, text: string): string | null {
  const d = waDigits(phone);
  return d ? `https://wa.me/${d}?text=${encodeURIComponent(text)}` : null;
}
